import React, { useState, useMemo } from "react";
import {
  View,
  Image,
  TouchableOpacity,
  Modal,
  Text,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { styles as customStyles } from "../../style/FoundryStyle";
import { backgroundColor2 } from "../../constants/Constants";
import { BACKGROUND_COLORS } from "../../style/colors.js";
import { extractPdfByReference } from "../../services/api/pdf/PdfExtractorApi";

const DocumentPreview = ({ document, data }) => {

  const [modalVisible, setModalVisible] = useState(false);
  const [pdfUrlToShow, setPdfUrlToShow] = useState(null);
  const [loading, setLoading] = useState(false);

  // Memoizar valores calculados para evitar recálculos innecesarios
  const { extension, documentUrl, fullTitle, extractedDate, iconSource } = useMemo(() => {
    const baseUrl =
      "https://catalog.facetbarcelona.com/images/database-odoo/documentacion/";
    const ext = document.split(".").pop().toLowerCase();
    const url = baseUrl + document;

    // Procesar título del documento
    const title = document.includes("DCP")
      ? document.substring(document.indexOf("DCP") + 3).trim()
      : document;

    // Extraer fecha del nombre del archivo (buscar patrones como DD-MM-YYYY o YYYY-MM-DD)
    const extractDate = (filename) => {
      // Buscar patrones de fecha: DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD, etc.
      const datePatterns = [
        /(\d{2}[-\/]\d{2}[-\/]\d{4})/g, // DD-MM-YYYY o DD/MM/YYYY
        /(\d{4}[-\/]\d{2}[-\/]\d{2})/g, // YYYY-MM-DD o YYYY/MM/DD
        /(\d{2}[-\/]\d{2}[-\/]\d{2})/g, // DD-MM-YY o DD/MM/YY
      ];
      
      for (const pattern of datePatterns) {
        const match = filename.match(pattern);
        if (match && match[0]) {
          return match[0].replace(/\//g, '-'); // Normalizar a guiones
        }
      }
      return null;
    };

    const extractedDate = extractDate(document);

    // Determinar icono según extensión
    const getIconSource = (ext) => {
      switch (ext) {
        case "jpg":
        case "png":
          return { uri: url };
        case "pdf":
        default:
          return require("../../assets/pdf.png");
      }
    };

    return {
      extension: ext,
      documentUrl: url,
      fullTitle: title,
      extractedDate: extractedDate,
      iconSource: getIconSource(ext),
    };
  }, [document]);

  const closeModal = () => {
    setModalVisible(false);
    setPdfUrlToShow(null);
    setLoading(false);
  };

  const extractReferenceFromImage = (imageUrl) => {
    if (!imageUrl) return null;
    try {
      const filename = imageUrl.split("/").pop();
      const match = filename.match(/^([A-Z0-9]{5})/i);
      return match ? match[1].toUpperCase() : null;
    } catch (error) {
      return null;
    }
  };

  const openModal = async () => {
    // Si es PDF y tenemos data con imagen, intentar extraer el PDF específico
    if (extension === "pdf" && data?.image) {
      const referencia = extractReferenceFromImage(data.image);

      if (referencia) {
        setLoading(true);
        try {
          const filename = await extractPdfByReference(documentUrl, referencia);
          if (filename) {
            // El filename ya viene con extensión .pdf del servidor
            const processedUrl = `https://new.facetbarcelona.com/JTS/php/temp/${filename}`;
            setPdfUrlToShow(processedUrl);
            setLoading(false);
            setModalVisible(true);
            return;
          }
        } catch (error) {
          console.error("Error al extraer PDF:", error);
        } finally {
          setLoading(false);
        }
      }
    }
    
    // Fallback: abrir PDF original o imagen normal
    setPdfUrlToShow(null);
    setModalVisible(true);
  };

  // Componente de contenido del modal optimizado
  const ModalContent = () => {
    const urlToShow = pdfUrlToShow || documentUrl;
    
    return (
      <View style={styles.modalContainer}>
        {/* Botón de cerrar en esquina superior derecha */}
        <TouchableOpacity 
          style={styles.closeButtonTop} 
          onPress={closeModal}
          activeOpacity={0.8}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <MaterialIcons name="close" size={22} color="#334155" />
        </TouchableOpacity>
        
        {/* Contenido del documento */}
        <View style={styles.documentContent}>
          {extension === "pdf" ? (
            <div
              style={styles.iframeContainer}
              onClick={(e) => e.stopPropagation()}
              dangerouslySetInnerHTML={{
                __html: `<iframe src="${urlToShow}" style="width:100%; height:100%;" frameBorder="0"></iframe>`,
              }}
            />
          ) : (
            <Image
              source={{ uri: documentUrl }}
              style={styles.modalImage}
              resizeMode='contain'
            />
          )}
        </View>
      </View>
    );
  };

  return (
    <View style={styles.previewWrapper}>
      <TouchableOpacity 
        onPress={openModal} 
        style={styles.previewContainer}
        disabled={loading}>
        <View style={styles.imageContainer}>
          <Image source={iconSource} style={styles.previewImage} />
        </View>
        <View style={styles.textContainer}>
          <Text style={styles.documentName} numberOfLines={2}>{fullTitle}</Text>
          {extractedDate && (
            <Text style={styles.documentDate}>{extractedDate}</Text>
          )}
        </View>
        {loading && (
          <ActivityIndicator 
            size="small" 
            color={backgroundColor2} 
            style={styles.loadingIndicator}
          />
        )}
      </TouchableOpacity>

      <Modal
        visible={modalVisible}
        onRequestClose={closeModal}
        transparent={true}
        animationType='fade'>
        <TouchableOpacity 
          style={styles.modalBackdrop} 
          activeOpacity={1}
          onPress={closeModal}>
          <View style={styles.modalContentWrapper} onStartShouldSetResponder={() => true}>
            <ModalContent />
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  previewWrapper: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    width: "100%",
  },
  previewContainer: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    marginVertical: 2,
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    borderRadius: 5,
  },
  imageContainer: {
    width: 40,
    height: 40,
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 10,
    padding: 2,
  },
  previewImage: {
    width: 32,
    height: 32,
    resizeMode: "contain",
  },
  textContainer: {
    flex: 1,
    justifyContent: "center",
  },
  documentName: {
    color: "black",
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 18,
    marginBottom: 2,
  },
  documentDate: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: "400",
  },
  loadingIndicator: {
    marginLeft: 10,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContentWrapper: {
    width: "98%",
    height: "98%",
    maxWidth: "none",
    maxHeight: "98vh",
  },
  modalContainer: {
    flex: 1,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  documentContent: {
    flex: 1,
    width: "100%",
  },
  iframeContainer: {
    flex: 1,
    width: "100%",
  },
  modalImage: {
    flex: 1,
    width: "100%",
  },
  closeButtonTop: {
    position: "absolute",
    top: 60,
    right: 16,
    zIndex: 1000,
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    borderWidth: 1,
    borderColor: "#cbd5e1",
  },
});

export default DocumentPreview;
